<template>
  <div>
    <OfferTermsCardComponent
      ref="termsCard"
      :registration-authority="selectionAuthority"
      :has-selection
      :price-hint="$t('soldera_inventory.forward.price_hint')"
    />

    <OfferActionsComponent
      :title="$t('soldera_inventory.forward.select_pools')"
      :can-submit
      :is-submitting
      :blocking-reason
      :show-blocking-reason="hasSelection"
      :sent="sentOffer"
      @submit="sendOffer"
    />

    <div class="mb-3">
      <PickerProductionRangeComponent v-model="periodForm" />
    </div>

    <div class="d-flex flex-column gap-3">
      <SelectionSummaryComponent
        :available="{
          mwh: sumBy(visiblePools, 'capacity_total'),
          count: visiblePools.length,
        }"
        :selected="{ mwh: selectedMwh, count: takenPools.length }"
      >
        <BreakdownSectionComponent
          v-for="section in breakdownSections"
          :key="section.key"
          :title="section.title"
          :entries="section.entries"
          :total="breakdownTotal"
          :data-testid="`breakdown-${section.key}`"
        />
      </SelectionSummaryComponent>

      <div
        v-for="warning in productWarnings"
        :key="warning.kind"
        class="alert alert-warning py-2 mb-0 fs-9"
        role="status"
        :data-testid="`product-warning-${warning.kind}`"
      >
        <FontAwesomeIcon icon="fa-solid fa-circle-info" class="me-2" />
        {{ warningText(warning) }}
      </div>

      <AutoSelectToolbarComponent
        v-model:enabled="autoSelectEnabled"
        v-model:target-mwh="targetMwh"
        v-model:spread="spreadOverPools"
        :spread-label="$t('soldera_inventory.forward.spread_over_pools')"
        :can-clear="hasSelection"
        data-testid="forward-filters"
        @clear="takes = {}"
      >
        <select
          v-model="technologyFilter"
          class="form-select form-select-sm w-auto"
          :aria-label="$t('soldera_inventory.forward.columns.technology')"
        >
          <option :value="null">
            {{ $t("soldera_inventory.forward.all_technologies") }}
          </option>
          <option
            v-for="technology in technologyOptions"
            :key="technology"
            :value="technology"
          >
            {{ technologyName(technology) }}
          </option>
        </select>
        <select
          v-model="countryFilter"
          class="form-select form-select-sm w-auto"
          :aria-label="$t('soldera_inventory.forward.columns.country')"
        >
          <option :value="null">
            {{ $t("soldera_inventory.forward.all_countries") }}
          </option>
          <option
            v-for="country in countryOptions"
            :key="country"
            :value="country"
          >
            {{ country }}
          </option>
        </select>
        <button
          type="button"
          class="btn btn-sm"
          :class="standardAibGc ? 'btn-primary' : 'btn-outline-secondary'"
          data-testid="standard-aib-gc-filter"
          @click="standardAibGc = !standardAibGc"
        >
          {{ $t("inventory_selection.standard_aib_gc") }}
        </button>
      </AutoSelectToolbarComponent>

      <LoadingIndicatorComponent v-if="isLoadingPools && pools.length === 0" />
      <ForwardPoolsTableComponent
        v-else
        v-model:takes="takes"
        :pools="visiblePools"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { sumBy, uniq } from "lodash-es";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { STANDARD_AIB_GC_COUNTRIES } from "@/constants";
import {
  distributeTakes,
  type TakeMap,
} from "@/forwards2/picker/distributeTakes";
import PickerProductionRangeComponent from "@/forwards2/picker/PickerProductionRangeComponent.vue";
import { defaultPeriodForm, periodRange } from "@/forwards2/picker/pickerForm";
import { volumeByVintage } from "@/forwards2/picker/productionBreakdown";
import AutoSelectToolbarComponent from "@/inventory_selection/AutoSelectToolbarComponent.vue";
import BreakdownSectionComponent, {
  type BreakdownEntry,
} from "@/inventory_selection/BreakdownSectionComponent.vue";
import { groupMwh } from "@/inventory_selection/groupMwh";
import SelectionSummaryComponent from "@/inventory_selection/SelectionSummaryComponent.vue";
import { useAutoSelect } from "@/inventory_selection/useAutoSelect";
import { technologyName } from "@/types/technology";

import { offerBlockingKey, singleAuthority } from "../common/offerGate";
import OfferActionsComponent from "../common/OfferActionsComponent.vue";
import OfferTermsCardComponent from "../common/OfferTermsCardComponent.vue";
import { useLatestListing } from "../common/useLatestListing";
import { useOfferTerms } from "../common/useOfferTerms";
import { solderaInventoryApi } from "../solderaInventoryApi";
import type {
  ForwardInventoryPool,
  ForwardInventoryResponse,
  OfferResponse,
} from "../solderaInventoryTypes";

import ForwardPoolsTableComponent from "./ForwardPoolsTableComponent.vue";
import { type SelectionWarning, selectionWarnings } from "./selectionWarnings";

/**
 * Forward tab of Soldera Inventory: future production from Soldera's forward pools over the periods
 * the buyer picks, one take per pool, bid on with the shared offer terms. An offer becomes a
 * draft forward deal that Soldera staff confirm.
 */

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const terms = useOfferTerms();
const termsCard =
  useTemplateRef<InstanceType<typeof OfferTermsCardComponent>>("termsCard");

const periodForm = ref(defaultPeriodForm());
const pools = ref<ForwardInventoryPool[]>([]);
// The periods the listed pools were computed for; an offer is made against these, not the form.
const listedPeriods = ref<string[]>([]);
const takes = ref<TakeMap>({});
const technologyFilter = ref<string | null>(null);
const countryFilter = ref<string | null>(null);
const standardAibGc = ref(false);
const spreadOverPools = ref(true);
const isSubmitting = ref(false);
const sentOffer = ref<OfferResponse | null>(null);

const pickedPeriods = computed(() => periodRange(periodForm.value));

// The pools follow the range and the price (a pool is listed only once the price clears its floor);
// until the refetch lands the listed pools are the old ones, so sending waits for it.
const {
  isLoading: isLoadingPools,
  listingFailed,
  refetch: fetchPools,
} = useLatestListing(
  () => {
    const periods = pickedPeriods.value;
    if (periods.length === 0) return null;
    return solderaInventoryApi.forward
      .listPools(periods, terms.listingPrice)
      .then(({ data }): ForwardInventoryResponse => data);
  },
  (data) => {
    pools.value = data?.pools ?? [];
    listedPeriods.value = data?.periods ?? [];
    takes.value = keepAvailableTakes(takes.value, pools.value);
  },
  {
    sources: [pickedPeriods, () => terms.listingPrice],
    debounceMs: 400,
    onError: globalErrorHandler,
  }
);

const technologyOptions = computed(() =>
  uniq(pools.value.flatMap((pool) => pool.technologies)).sort()
);
const countryOptions = computed(() =>
  uniq(pools.value.flatMap((pool) => pool.countries)).sort()
);

const visiblePools = computed(() =>
  pools.value.filter(
    (pool) =>
      (technologyFilter.value === null ||
        pool.technologies.includes(technologyFilter.value)) &&
      (countryFilter.value === null ||
        pool.countries.includes(countryFilter.value)) &&
      (!standardAibGc.value || isStandardAibGc(pool))
  )
);

// Standard AIB GC: AIB certificates from the countries buyers accept as one standard product.
function isStandardAibGc(pool: ForwardInventoryPool): boolean {
  return (
    pool.registration_authority === "AIB" &&
    pool.countries.every((country) =>
      STANDARD_AIB_GC_COUNTRIES.includes(country)
    )
  );
}

const takenPools = computed(() =>
  pools.value.filter((pool) => (takes.value[pool.id] ?? 0) > 0)
);
const hasSelection = computed(() => takenPools.value.length > 0);
const productWarnings = computed(() => selectionWarnings(takenPools.value));
const selectedMwh = computed(() =>
  sumBy(takenPools.value, (pool) => takes.value[pool.id] ?? 0)
);

// Auto-select works as on the Spot tab, filling the target from the pools on screen.
const { enabled: autoSelectEnabled, targetMwh } = useAutoSelect({
  run: autoDistribute,
  sources: [spreadOverPools, visiblePools],
});

const selectedAuthorities = computed(
  () => new Set(takenPools.value.map((pool) => pool.registration_authority))
);

// The scheme the counterparty's registry account must belong to; the picker validates against it.
const selectionAuthority = computed(() =>
  singleAuthority(selectedAuthorities.value)
);

// What the breakdowns describe: the takes once there are any, else what the filters show.
const breakdownPools = computed(() =>
  hasSelection.value ? takenPools.value : visiblePools.value
);
const poolMwh = (pool: ForwardInventoryPool): number =>
  hasSelection.value ? (takes.value[pool.id] ?? 0) : pool.capacity_total;
const breakdownTotal = computed(() => sumBy(breakdownPools.value, poolMwh));

const breakdownSections = computed(() => [
  {
    key: "vintage",
    title: t("soldera_inventory.forward.by_period"),
    entries: volumeByVintage(
      breakdownPools.value.map((pool) => ({
        take: poolMwh(pool),
        capacity_by_period: pool.capacity_by_period,
      }))
    ),
  },
  {
    key: "technology",
    title: t("inventory_selection.breakdowns.technology"),
    entries: breakdownEntries((pool) =>
      pool.technologies.map(technologyName).join(", ")
    ),
  },
  {
    key: "countries",
    title: t("soldera_inventory.forward.columns.country"),
    entries: breakdownEntries((pool) => pool.countries.join(", ")),
  },
  {
    key: "support",
    title: t("inventory_selection.breakdowns.support_status"),
    entries: breakdownEntries((pool) =>
      t(`forwards.common.support.${pool.support}`)
    ),
  },
]);

const blockingReason = computed((): string | null => {
  const key = offerBlockingKey({
    hasSelection: hasSelection.value,
    noSelectionKey: "soldera_inventory.forward.blocked.no_takes",
    termsBlockingKey: terms.blockingKey,
    authorities: selectedAuthorities.value,
  });
  return key && t(key);
});

const canSubmit = computed(
  () =>
    blockingReason.value === null &&
    !isSubmitting.value &&
    !isLoadingPools.value &&
    !listingFailed.value
);

function breakdownEntries(
  keyOf: (pool: ForwardInventoryPool) => string
): BreakdownEntry[] {
  return [
    ...groupMwh(breakdownPools.value, (pool) => keyOf(pool) || "–", poolMwh),
  ]
    .sort(([, a], [, b]) => b - a)
    .map(([label, mwh]) => ({ label, mwh }));
}

function warningText(warning: SelectionWarning): string {
  const key = `soldera_inventory.forward.warnings.${warning.kind}`;
  const list = (values: string[]) => values.join(", ");
  const technologies = (values: string[]) =>
    list(values.map((value) => technologyName(value)));
  if (warning.kind === "pl_be_mixed") {
    return t(key, {
      flagged: list(warning.flagged),
      others: list(warning.others),
    });
  }
  if (warning.kind === "standard_aib_gc_mixed") {
    return t(key, {
      standard: list(warning.standard),
      outside: list(warning.outside),
    });
  }
  return t(key, {
    advanced: technologies(warning.advanced),
    hsw: technologies(warning.hsw),
  });
}

// A take survives a refetch only on a pool still listed, and never above what that pool can now sell.
function keepAvailableTakes(
  current: TakeMap,
  listed: ForwardInventoryPool[]
): TakeMap {
  const next: TakeMap = {};
  for (const pool of listed) {
    const take = Math.min(current[pool.id] ?? 0, pool.capacity_total);
    if (take > 0) next[pool.id] = take;
  }
  return next;
}

function autoDistribute(targetMwh: number) {
  const rows = visiblePools.value.map((pool) => ({
    pool_id: pool.id,
    capacity_total: pool.capacity_total,
    // A buyer never sees a pool's floor, so the greedy fill orders by size alone.
    minimum_price: null,
  }));
  takes.value = distributeTakes(
    rows,
    targetMwh,
    spreadOverPools.value,
    takes.value
  );
}

async function sendOffer() {
  if (!canSubmit.value || !termsCard.value?.validate()) return;
  isSubmitting.value = true;
  try {
    const { data } = await solderaInventoryApi.forward.createOffer({
      periods: listedPeriods.value,
      takes: takes.value,
      ...terms.toRequest(),
    });
    globalSuccessHandler(
      t("soldera_inventory.created_title"),
      t("soldera_inventory.created_description", { mwh: data.volume_mwh })
    );
    sentOffer.value = data;
    takes.value = {};
    await fetchPools();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>
